import React, { useState, useRef, useCallback } from 'react';
import {
  AppBar, Toolbar, Box, Button, IconButton, Typography, Drawer,
  List, ListItem, Divider, useScrollTrigger, Slide,
  useTheme, ListItemButton, ListItemText, Container, Paper,
} from '@mui/material';
import {
  Menu as MenuIcon, Close as CloseIcon,
  LocalHospital, Warning as WarningIcon,
  KeyboardArrowRight, KeyboardArrowDown,
  Facebook, Instagram, Search, WbSunny, DarkMode,
} from '@mui/icons-material';
import { useNavigate, useLocation, Link } from 'react-router-dom';
import { useLanguage } from '../../contexts/LanguageContext';
import { useThemeContext } from '../../contexts/ThemeContext';
import { Language } from '../../i18n/translations';
import { emergencyNumbers, departments } from '../../data/hospitalData';

// ─── Types ────────────────────────────────────────────────────────────────────

interface DropdownItem {
  label: string;
  path: string;
  hasArrow?: boolean;
}

type NavGroup =
  | { type: 'link'; key: string; path: string; label: string }
  | { type: 'dropdown'; key: string; label: string; items: DropdownItem[] };

// ─── Nav structure ────────────────────────────────────────────────────────────

const buildNavGroups = (t: any, lang: 'en' | 'fr'): NavGroup[] => [
  { type: 'link', key: 'home', path: '/', label: t.nav.home },
  { type: 'link', key: 'contact', path: '/contact', label: t.nav.contact },
  {
    type: 'dropdown',
    key: 'departments',
    label: t.nav.departments,
    items: departments.slice(0, 6).map((d) => ({
      label: lang === 'en' ? d.nameEn : d.nameFr,
      path: `/departments/${d.id}`,
      hasArrow: true,
    })),
  },
  {
    type: 'dropdown',
    key: 'about',
    label: t.nav.about,
    items: [
      { label: lang === 'en' ? 'About Us' : 'À propos', path: '/about' },
      { label: lang === 'en' ? 'Our Doctors' : 'Nos Médecins', path: '/doctors', hasArrow: true },
    ],
  },
  {
    type: 'dropdown',
    key: 'more',
    label: lang === 'en' ? 'More' : 'Plus',
    items: [
      { label: t.nav.patients, path: '/patients' },
      { label: t.nav.news, path: '/news' },
      { label: t.nav.appointment, path: '/appointment' },
      { label: t.nav.emergency, path: '/emergency' },
    ],
  },
];

// ─── Hover Dropdown ───────────────────────────────────────────────────────────

interface DropdownMenuProps {
  items: DropdownItem[];
  anchorEl: HTMLElement | null;
  onNavigate: (path: string) => void;
  onMouseEnter: () => void;
  onMouseLeave: () => void;
}

const DropdownMenu: React.FC<DropdownMenuProps> = ({
  items, anchorEl, onNavigate, onMouseEnter, onMouseLeave,
}) => {
  const theme = useTheme();
  if (!anchorEl) return null;

  const rect = anchorEl.getBoundingClientRect();

  return (
    <Paper
      elevation={4}
      onMouseEnter={onMouseEnter}
      onMouseLeave={onMouseLeave}
      sx={{
        position: 'fixed',
        top: rect.bottom,
        left: rect.left,
        minWidth: 240,
        zIndex: 1400,
        borderRadius: 0,
        border: `1px solid ${theme.palette.divider}`,
        overflow: 'hidden',
        py: 0.5,
      }}
    >
      {items.map((item, i) => (
        <Box
          key={i}
          onClick={() => onNavigate(item.path)}
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            px: 3,
            py: 1.5,
            cursor: 'pointer',
            color: 'primary.main',
            fontWeight: 600,
            fontSize: '0.9rem',
            borderBottom: i < items.length - 1 ? `1px solid ${theme.palette.divider}` : 'none',
            '&:hover': {
              bgcolor: 'primary.main',
              color: 'white',
            },
            transition: 'all 0.15s ease',
          }}
        >
          {item.label}
          {item.hasArrow && <KeyboardArrowRight sx={{ fontSize: 18, opacity: 0.7 }} />}
        </Box>
      ))}
    </Paper>
  );
};

// ─── Main Navbar ──────────────────────────────────────────────────────────────

const Navbar: React.FC = () => {
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [openKey, setOpenKey] = useState<string | null>(null);
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const closeTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  const { t, language, setLanguage } = useLanguage();
  const { mode, toggleTheme } = useThemeContext();
  const theme = useTheme();
  const navigate = useNavigate();
  const location = useLocation();
  const trigger = useScrollTrigger();

  const navGroups = buildNavGroups(t, language as 'en' | 'fr');

  const isActive = (path: string) =>
    path === '/' ? location.pathname === '/' : location.pathname.startsWith(path);

  const handleNavClick = (path: string) => {
    navigate(path);
    setDrawerOpen(false);
    closeDropdown();
  };

  const toggleLang = () => setLanguage(language === 'en' ? 'fr' : ('en' as Language));

  const openDropdown = useCallback((key: string, el: HTMLElement) => {
    if (closeTimer.current) clearTimeout(closeTimer.current);
    setOpenKey(key);
    setAnchorEl(el);
  }, []);

  const scheduleClose = useCallback(() => {
    closeTimer.current = setTimeout(() => {
      setOpenKey(null);
      setAnchorEl(null);
    }, 150);
  }, []);

  const cancelClose = useCallback(() => {
    if (closeTimer.current) clearTimeout(closeTimer.current);
  }, []);

  const closeDropdown = useCallback(() => {
    if (closeTimer.current) clearTimeout(closeTimer.current);
    setOpenKey(null);
    setAnchorEl(null);
  }, []);

  const activeDropdownItems =
    openKey
      ? (navGroups.find(
          (g) => g.key === openKey && g.type === 'dropdown'
        ) as Extract<NavGroup, { type: 'dropdown' }> | undefined)?.items ?? []
      : [];

  return (
    <>
      <Slide appear={false} direction="down" in={!trigger}>
        <AppBar
          position="sticky"
          color="default"
          elevation={0}
          sx={{
            bgcolor: theme.palette.mode === 'dark' ? 'background.paper' : 'white',
            borderBottom: `1px solid ${theme.palette.divider}`,
          }}
        >
          <Container maxWidth="xl">
            <Toolbar sx={{ py: 0.5, minHeight: { xs: 64, sm: 72 }, px: { xs: 0 } }}>

              {/* Logo */}
              <Box
                component={Link}
                to="/"
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 1.5,
                  textDecoration: 'none',
                  flexShrink: 0,
                  mr: 4,
                }}
              >
                <Box
                  sx={{
                    bgcolor: 'primary.main',
                    color: 'white',
                    borderRadius: 2,
                    p: 0.75,
                    display: 'flex',
                    alignItems: 'center',
                  }}
                >
                  <LocalHospital sx={{ fontSize: 28 }} />
                </Box>
                <Box sx={{ display: { xs: 'none', sm: 'block' } }}>
                  <Typography
                    variant="subtitle1"
                    sx={{ fontWeight: 800, color: 'primary.main', lineHeight: 1.1, fontSize: '0.88rem' }}
                  >
                    Hôpital Régional
                  </Typography>
                  <Typography
                    variant="caption"
                    sx={{ color: 'text.secondary', fontWeight: 500, fontSize: '0.70rem', letterSpacing: '0.08em' }}
                  >
                    DE BAFOUSSAM
                  </Typography>
                </Box>
              </Box>

              {/* Desktop nav */}
              <Box
                sx={{
                  display: { xs: 'none', lg: 'flex' },
                  alignItems: 'center',
                  flexGrow: 1,
                }}
              >
                {navGroups.map((group) => {
                  if (group.type === 'link') {
                    return (
                      <Button
                        key={group.key}
                        onClick={() => handleNavClick(group.path)}
                        disableRipple
                        sx={{
                          color: isActive(group.path) ? 'primary.main' : 'text.primary',
                          fontWeight: isActive(group.path) ? 700 : 500,
                          fontSize: '0.9rem',
                          px: 2.5,
                          py: 1,
                          borderRadius: 0,
                          borderBottom: isActive(group.path) ? '2px solid' : '2px solid transparent',
                          borderColor: isActive(group.path) ? 'primary.main' : 'transparent',
                          textTransform: 'none',
                          whiteSpace: 'nowrap',
                          '&:hover': { bgcolor: 'transparent', color: 'primary.main' },
                        }}
                      >
                        {group.label}
                      </Button>
                    );
                  }

                  const isOpen = openKey === group.key;
                  const groupActive = group.items.some((i) => isActive(i.path));

                  return (
                    <Button
                      key={group.key}
                      disableRipple
                      endIcon={
                        <KeyboardArrowDown
                          sx={{
                            fontSize: '18px !important',
                            transition: 'transform 0.2s',
                            transform: isOpen ? 'rotate(180deg)' : 'none',
                          }}
                        />
                      }
                      onMouseEnter={(e) => openDropdown(group.key, e.currentTarget)}
                      onMouseLeave={scheduleClose}
                      sx={{
                        color: groupActive || isOpen ? 'primary.main' : 'text.primary',
                        fontWeight: groupActive || isOpen ? 700 : 500,
                        fontSize: '0.9rem',
                        px: 2.5,
                        py: 1,
                        borderRadius: 0,
                        borderBottom: groupActive ? '2px solid' : '2px solid transparent',
                        borderColor: groupActive ? 'primary.main' : 'transparent',
                        textTransform: 'none',
                        whiteSpace: 'nowrap',
                        '&:hover': { bgcolor: 'transparent', color: 'primary.main' },
                      }}
                    >
                      {group.label}
                    </Button>
                  );
                })}
              </Box>

              <Box sx={{ flexGrow: { xs: 1, lg: 0 } }} />

              {/* Right actions */}
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                <Button
                  size="small"
                  onClick={toggleLang}
                  sx={{
                    display: { xs: 'none', md: 'flex' },
                    minWidth: 0,
                    px: 1.25,
                    py: 0.5,
                    fontWeight: 700,
                    fontSize: '0.75rem',
                    border: '1.5px solid',
                    borderColor: 'divider',
                    borderRadius: 1,
                    color: 'text.secondary',
                    textTransform: 'none',
                    '&:hover': { borderColor: 'primary.main', color: 'primary.main' },
                  }}
                >
                  {language === 'en' ? 'FR' : 'EN'}
                </Button>

                <IconButton onClick={toggleTheme} size="small" color="inherit">
                  {mode === 'light' ? <DarkMode sx={{ fontSize: 20 }} /> : <WbSunny sx={{ fontSize: 20 }} />}
                </IconButton>

                <IconButton
                  size="small"
                  color="inherit"
                  sx={{ display: { xs: 'none', md: 'flex' }, '&:hover': { color: '#1877F2' } }}
                >
                  <Facebook sx={{ fontSize: 20 }} />
                </IconButton>

                <IconButton
                  size="small"
                  color="inherit"
                  sx={{ display: { xs: 'none', md: 'flex' }, '&:hover': { color: '#E1306C' } }}
                >
                  <Instagram sx={{ fontSize: 20 }} />
                </IconButton>

                <IconButton
                  size="small"
                  color="inherit"
                  sx={{ display: { xs: 'none', md: 'flex' }, '&:hover': { color: 'primary.main' } }}
                >
                  <Search sx={{ fontSize: 20 }} />
                </IconButton>

                <IconButton
                  sx={{ display: { xs: 'flex', lg: 'none' }, ml: 0.5 }}
                  onClick={() => setDrawerOpen(true)}
                  color="inherit"
                >
                  <MenuIcon />
                </IconButton>
              </Box>
            </Toolbar>
          </Container>
        </AppBar>
      </Slide>

      {/* Hover dropdown */}
      {openKey && (
        <DropdownMenu
          items={activeDropdownItems}
          anchorEl={anchorEl}
          onNavigate={handleNavClick}
          onMouseEnter={cancelClose}
          onMouseLeave={scheduleClose}
        />
      )}

      {/* Mobile Drawer */}
      <Drawer
        anchor="right"
        open={drawerOpen}
        onClose={() => setDrawerOpen(false)}
        PaperProps={{ sx: { width: 290 } }}
      >
        <Box sx={{ p: 2, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <Typography variant="h6" fontWeight={700} color="primary.main">
            Menu
          </Typography>
          <IconButton onClick={() => setDrawerOpen(false)}>
            <CloseIcon />
          </IconButton>
        </Box>
        <Divider />

        <List dense>
          {navGroups.map((group) => {
            if (group.type === 'link') {
              return (
                <ListItem key={group.key} disablePadding>
                  <ListItemButton
                    onClick={() => handleNavClick(group.path)}
                    selected={isActive(group.path)}
                    sx={{ px: 3 }}
                  >
                    <ListItemText
                      primary={group.label}
                      primaryTypographyProps={{ fontWeight: isActive(group.path) ? 700 : 400 }}
                    />
                  </ListItemButton>
                </ListItem>
              );
            }

            return (
              <React.Fragment key={group.key}>
                <ListItem disablePadding>
                  <ListItemButton sx={{ px: 3 }} disabled>
                    <ListItemText
                      primary={group.label}
                      primaryTypographyProps={{
                        fontWeight: 700,
                        color: 'primary.main',
                        fontSize: '0.85rem',
                      }}
                    />
                  </ListItemButton>
                </ListItem>
                {group.items.map((item, i) => (
                  <ListItem key={i} disablePadding>
                    <ListItemButton onClick={() => handleNavClick(item.path)} sx={{ pl: 5 }}>
                      <ListItemText
                        primary={item.label}
                        primaryTypographyProps={{ fontSize: '0.88rem' }}
                      />
                      {item.hasArrow && (
                        <KeyboardArrowRight sx={{ fontSize: 16, color: 'text.disabled' }} />
                      )}
                    </ListItemButton>
                  </ListItem>
                ))}
                <Divider sx={{ my: 0.5 }} />
              </React.Fragment>
            );
          })}
        </List>

        <Box sx={{ p: 2, display: 'flex', gap: 1 }}>
          <Button
            variant="outlined"
            size="small"
            onClick={toggleLang}
            sx={{ flex: 1, textTransform: 'none' }}
          >
            {language === 'en' ? 'Français' : 'English'}
          </Button>
          <IconButton onClick={toggleTheme} size="small">
            {mode === 'light' ? <DarkMode /> : <WbSunny />}
          </IconButton>
        </Box>

        <Box sx={{ px: 2, pb: 2, display: 'flex', flexDirection: 'column', gap: 1 }}>
          <Button
            variant="contained"
            color="primary"
            fullWidth
            onClick={() => handleNavClick('/appointment')}
            sx={{ textTransform: 'none' }}
          >
            {t.common.bookAppointment}
          </Button>
          <Button
            variant="outlined"
            color="error"
            fullWidth
            onClick={() => handleNavClick('/emergency')}
            startIcon={<WarningIcon />}
            sx={{ textTransform: 'none' }}
          >
            {t.common.emergency}
          </Button>
        </Box>
      </Drawer>
    </>
  );
};

export default Navbar;
